!function () {
  "use strict";

  if (customElements.get("shop-bar")) return;

  customElements.define("shop-bar", class extends HTMLElement {
    constructor() {
      super();

      this.section = null;
      this.form = null;
      this.formWrapper = null;
      this.submitButton = null;
      this.submitButtonText = null;
      this.unsubscribers = [];
      this.observer = null;
      this.fallbackTimer = 0;

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundToggleCartBarOnScroll = this.toggleCartBarOnScroll.bind(this);
      this.boundSetCartBarHeight = this.setCartBarHeight.bind(this);
      this.boundSyncFromMainForm = this.syncFromMainForm.bind(this);
      this.boundHandleCartComplete = this.handleCartComplete.bind(this);
      this.boundHandleProductAddError = this.handleProductAddError.bind(this);
    }

    connectedCallback() {
      this.section = this.closest("product-info");
      this.form = this.section ? this.section.querySelector("[data-product-form]") : null;
      this.formWrapper = window.theme && window.theme.settings && window.theme.settings.productPageSticky && this.section
        ? this.section.querySelector(".product__info") || this.form
        : this.form;
      this.submitButton = this.querySelector("[data-cart-bar-add-to-cart]");
      this.submitButtonText = this.querySelector("[data-add-to-cart-text]");

      this.setCartBarHeight();
      this.syncFromMainForm();
      this.observeMainButton();

      this.addEventListener("click", this.boundHandleClick);
      document.addEventListener("theme:scroll", this.boundToggleCartBarOnScroll);
      document.addEventListener("theme:resize", this.boundSetCartBarHeight);
      document.addEventListener("theme:product:add-error", this.boundHandleProductAddError);

      this.subscribeToThemeEvents();
      this.toggleCartBarOnScroll();
    }

    getMainButton() {
      return this.form ? this.form.querySelector("[data-add-to-cart]") : null;
    }

    observeMainButton() {
      const mainButton = this.getMainButton();
      if (!mainButton || typeof MutationObserver === "undefined") return;

      this.observer = new MutationObserver(this.boundSyncFromMainForm);
      this.observer.observe(mainButton, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ["disabled", "class"]
      });
    }

    subscribeToThemeEvents() {
      if (typeof window.subscribe !== "function") return;

      const events = window.theme && window.theme.PUB_SUB_EVENTS ? window.theme.PUB_SUB_EVENTS : {};
      const variantChange = events.variantChange || "variant-change";
      const cartUpdate = events.cartUpdate || "cart-update";
      const cartError = events.cartError || "cart-error";

      this.unsubscribers.push(window.subscribe(variantChange, this.boundSyncFromMainForm));
      this.unsubscribers.push(window.subscribe(cartUpdate, this.boundHandleCartComplete));
      this.unsubscribers.push(window.subscribe(cartError, this.boundHandleProductAddError));
    }

    handleClick(event) {
      const stickyButton = event.target.closest("[data-cart-bar-add-to-cart]");
      if (!stickyButton) return;

      event.preventDefault();
      this.syncFromMainForm();

      const mainButton = this.getMainButton();
      if (!mainButton || mainButton.disabled || mainButton.hasAttribute("disabled")) {
        this.scrollToTop();
        return;
      }

      stickyButton.classList.add("is-loading");
      stickyButton.setAttribute("disabled", "disabled");
      mainButton.click();

      window.clearTimeout(this.fallbackTimer);
      this.fallbackTimer = window.setTimeout(this.boundHandleCartComplete, 1600);
    }

    syncFromMainForm() {
      if (!this.submitButton) return;

      const mainButton = this.getMainButton();
      const mainText = mainButton ? mainButton.querySelector("[data-add-to-cart-text]") : null;

      if (mainText && this.submitButtonText && this.submitButtonText.innerHTML !== mainText.innerHTML) {
        this.submitButtonText.innerHTML = mainText.innerHTML;
      }

      const mainButtonDisabled = !mainButton || mainButton.disabled || mainButton.hasAttribute("disabled");
      if (mainButtonDisabled) {
        this.submitButton.setAttribute("disabled", "disabled");
      } else if (!this.submitButton.classList.contains("is-loading")) {
        this.submitButton.removeAttribute("disabled");
      }

      this.submitButton.classList.toggle("is-loading", Boolean(mainButton && mainButton.classList.contains("is-loading")));
      this.setCartBarHeight();
    }

    handleCartComplete() {
      window.clearTimeout(this.fallbackTimer);

      if (this.submitButton) {
        this.submitButton.classList.remove("is-loading");
      }

      this.syncFromMainForm();
    }

    handleProductAddError() {
      this.handleCartComplete();
      this.scrollToTop();
    }

    scrollToTop() {
      if (!this.section) return;

      const variantSelects = this.section.querySelector("variant-selects");
      const isMobile = window.theme && typeof window.theme.isMobile === "function" && window.theme.isMobile();
      const target = isMobile ? variantSelects || this.form : this.section;
      if (!target) return;

      if (window.theme && typeof window.theme.scrollTo === "function") {
        const top = target.getBoundingClientRect().top;
        window.theme.scrollTo(isMobile ? top - 10 : top);
        return;
      }

      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    toggleCartBarOnScroll() {
      if (this.formWrapper) {
        const threshold = this.formWrapper.offsetTop + this.formWrapper.offsetHeight;
        this.classList.toggle("is-visible", window.scrollY > threshold);
      }

      this.syncFromMainForm();
    }

    setCartBarHeight() {
      const isMobile = window.theme && typeof window.theme.isMobile === "function" && window.theme.isMobile();
      const minimumHeight = isMobile ? 74 : 80;
      const height = Math.max(this.offsetHeight || 0, minimumHeight);

      document.documentElement.style.setProperty("--cart-bar-height", `${height}px`);
    }

    disconnectedCallback() {
      window.clearTimeout(this.fallbackTimer);
      this.removeEventListener("click", this.boundHandleClick);
      document.removeEventListener("theme:scroll", this.boundToggleCartBarOnScroll);
      document.removeEventListener("theme:resize", this.boundSetCartBarHeight);
      document.removeEventListener("theme:product:add-error", this.boundHandleProductAddError);

      if (this.observer) {
        this.observer.disconnect();
      }

      this.unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === "function") unsubscribe();
      });
    }
  });
}();
